import { datadogLogs } from '@datadog/browser-logs'
import {
  InboundEnvelopeSchema,
  PROTOCOL_VERSION,
  SOURCE_OPERATOR,
  SOURCE_WIDGET,
  type InboundCommandType,
  type PostMessageEnvelope
} from './types'

type InboundHandler = (payload: Record<string, unknown>, requestId?: string) => void | Promise<void>

const RATE_LIMIT_WINDOW_MS = 1000
const RATE_LIMIT_MAX_PER_WINDOW = 30
const BALANCE_REFRESH_DEBOUNCE_MS = 2000

/**
 * PostMessageBridge — singleton that manages bi-directional iframe postMessage communication.
 *
 * Inbound: operator sends commands (refresh-balance, mute-audio, etc.)
 * Outbound: widget emits events (bet:placed, balance:changed, etc.)
 *
 * Security: messages are validated with Zod, rate-limited, and source-checked.
 * Origin uses '*' since the widget serves all operators from a single deployment,
 * and the real auth boundary is the WebSocket session.
 */
export class PostMessageBridge {
  private static instance: PostMessageBridge | null = null

  private handlers = new Map<string, InboundHandler>()
  private isInIframe: boolean
  private isInitialized = false
  private messageQueue: MessageEvent[] = []
  private isReady = false

  // Rate limiting
  private rateBuckets = new Map<string, { count: number; windowStart: number }>()
  private lastBalanceRefresh = 0

  private constructor() {
    this.isInIframe = typeof window !== 'undefined' && window.parent !== window
  }

  static getInstance(): PostMessageBridge {
    if (!PostMessageBridge.instance) {
      PostMessageBridge.instance = new PostMessageBridge()
    }
    return PostMessageBridge.instance
  }

  init(): void {
    if (this.isInitialized || typeof window === 'undefined') return
    this.isInitialized = true

    window.addEventListener('message', this.handleInbound)
  }

  destroy(): void {
    if (!this.isInitialized || typeof window === 'undefined') return
    this.isInitialized = false

    window.removeEventListener('message', this.handleInbound)
    this.handlers.clear()
    this.rateBuckets.clear()
    this.messageQueue = []
    PostMessageBridge.instance = null
  }

  /**
   * Mark the bridge as ready — flushes any queued messages received before initialization.
   */
  setReady(): void {
    this.isReady = true
    for (const event of this.messageQueue) {
      this.processMessage(event)
    }
    this.messageQueue = []
  }

  registerHandler(type: InboundCommandType, handler: InboundHandler): void {
    this.handlers.set(type, handler)
  }

  /**
   * Send an outbound event to the parent frame.
   */
  send<T>(type: string, payload: T, requestId?: string): void {
    if (!this.isInIframe) return

    const envelope: PostMessageEnvelope<T> = {
      source: SOURCE_WIDGET,
      type,
      version: PROTOCOL_VERSION,
      payload,
      timestamp: Date.now(),
      ...(requestId && { requestId })
    }

    try {
      window.parent.postMessage(envelope, '*')
    } catch {
      // Silently ignore — parent may have navigated away
    }
  }

  // ── Private ──

  private handleInbound = (event: MessageEvent): void => {
    // Ignore messages not from the operator
    if (!event.data || event.data.source !== SOURCE_OPERATOR) return

    if (!this.isReady) {
      this.messageQueue.push(event)
      return
    }

    this.processMessage(event)
  }

  private processMessage(event: MessageEvent): void {
    // Validate envelope
    const result = InboundEnvelopeSchema.safeParse(event.data)
    if (!result.success) {
      datadogLogs.logger.warn('postmessage_invalid_envelope', {
        error: result.error.message
      })
      return
    }

    const { type, payload, requestId } = result.data

    // Rate limit
    if (!this.checkRateLimit(type)) {
      datadogLogs.logger.warn('postmessage_rate_limited', { type })
      return
    }

    // Debounce balance refresh
    if (type === 'refresh-balance') {
      const now = Date.now()
      if (now - this.lastBalanceRefresh < BALANCE_REFRESH_DEBOUNCE_MS) return
      this.lastBalanceRefresh = now
    }

    // Dispatch to handler
    const handler = this.handlers.get(type)
    if (!handler) return // Unknown commands silently ignored for forward compatibility

    try {
      const result = handler(payload, requestId)
      // Handle async handlers — catch rejected promises to prevent unhandled rejections
      if (result && typeof (result as Promise<void>).catch === 'function') {
        ;(result as Promise<void>).catch((err) => {
          datadogLogs.logger.error('postmessage_handler_error', {
            type,
            error: err instanceof Error ? err.message : String(err)
          })
        })
      }
    } catch (err) {
      datadogLogs.logger.error('postmessage_handler_error', {
        type,
        error: err instanceof Error ? err.message : String(err)
      })
    }
  }

  private checkRateLimit(type: string): boolean {
    const now = Date.now()
    const bucket = this.rateBuckets.get(type)

    if (!bucket || now - bucket.windowStart > RATE_LIMIT_WINDOW_MS) {
      this.rateBuckets.set(type, { count: 1, windowStart: now })
      return true
    }

    if (bucket.count >= RATE_LIMIT_MAX_PER_WINDOW) return false

    bucket.count++
    return true
  }
}
